import { useState, useEffect, useRef } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { format, parseISO } from "date-fns";
import { utcEntryDay } from "@/lib/trackerx-dates";

// Parse date string as local date (not UTC) to avoid timezone shifting
import { Plus, Trash2, Check, TrendingUp, Calculator } from "lucide-react";
const parseLocalDate = (dateStr: string): Date => {
  const [year, month, day] = dateStr.split("-").map(Number);
  return new Date(year, month - 1, day);
};

// Format a date from the database to yyyy-MM-dd for input fields
// Handles both Date objects and ISO strings, preserving the intended date
const formatDateForInput = (date: Date | string | null | undefined): string => {
  if (!date) return format(new Date(), "yyyy-MM-dd");
  
  // If it's a string in ISO format, extract just the date part
  if (typeof date === "string") {
    // Handle ISO format like "2026-01-01T00:00:00.000Z"
    const isoMatch = date.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (isoMatch) {
      return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;
    }
  }
  
  // For Date objects, format to local date
  const d = new Date(date);
  if (isNaN(d.getTime())) return format(new Date(), "yyyy-MM-dd");
  
  // Use UTC components to avoid timezone shifting
  const year = d.getUTCFullYear();
  const month = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import type { GoalProgressEntry, Goal } from "@shared/schema";

interface MeasuredTrackingSectionProps {
  goal: Goal;
}

interface EditedEntry {
  label: string;
  amount: string;
  date: string;
}

export default function MeasuredTrackingSection({ goal }: MeasuredTrackingSectionProps) {
  const { toast } = useToast();
  const [newEntryLabel, setNewEntryLabel] = useState("");
  const [newEntryAmount, setNewEntryAmount] = useState("");
  const [newEntryDate, setNewEntryDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [editedEntries, setEditedEntries] = useState<Record<string, EditedEntry>>({});
  const [trackingMode, setTrackingMode] = useState<"sum" | "trend">(
    (goal.measuredTrackingMode as "sum" | "trend") || "sum"
  );

  // Update local state when goal changes
  useEffect(() => {
    setTrackingMode((goal.measuredTrackingMode as "sum" | "trend") || "sum");
  }, [goal.measuredTrackingMode]);

  // Fetch progress entries
  const { data: entries = [], isLoading } = useQuery<GoalProgressEntry[]>({
    queryKey: ["/api/goals", goal.id, "progress-entries"],
    queryFn: async () => {
      const response = await fetch(`/api/goals/${goal.id}/progress-entries`);
      if (!response.ok) throw new Error("Failed to fetch progress entries");
      return response.json();
    },
  });

  // Update tracking mode mutation
  const updateTrackingModeMutation = useMutation({
    mutationFn: async (mode: "sum" | "trend") => {
      return apiRequest("PATCH", `/api/goals/${goal.id}`, { measuredTrackingMode: mode });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/goals", goal.id] });
      queryClient.invalidateQueries({ queryKey: ["/api/goals"] });
    },
    onError: () => {
      toast({ title: "Couldn't change tracking mode", description: "Your change was not saved. Please try again.", variant: "destructive" });
    },
  });

  // Create entry mutation
  const createEntryMutation = useMutation({
    mutationFn: async (data: { label: string; amount: number; date: Date }) => {
      return apiRequest("POST", `/api/goals/${goal.id}/progress-entries`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/goals", goal.id, "progress-entries"] });
      queryClient.invalidateQueries({ queryKey: ["/api/goals", goal.id] });
      queryClient.invalidateQueries({ queryKey: ["/api/goals"] });
      setNewEntryLabel("");
      setNewEntryAmount("");
      setNewEntryDate(format(new Date(), "yyyy-MM-dd"));
      toast({
        title: "Entry added",
        description: "Measurement entry has been added successfully.",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to add measurement entry.",
        variant: "destructive",
      });
    },
  });

  // Delete entry mutation
  const deleteEntryMutation = useMutation({
    mutationFn: async (entryId: string) => {
      return apiRequest("DELETE", `/api/goals/${goal.id}/progress-entries/${entryId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/goals", goal.id, "progress-entries"] });
      queryClient.invalidateQueries({ queryKey: ["/api/goals", goal.id] });
      queryClient.invalidateQueries({ queryKey: ["/api/goals"] });
      toast({
        title: "Entry deleted",
        description: "Measurement entry has been removed.",
      });
    },
    onError: () => {
      toast({ title: "Couldn't delete entry", description: "Please try again.", variant: "destructive" });
    },
  });

  // Update entry mutation
  const updateEntryMutation = useMutation({
    mutationFn: async ({ entryId, data }: { entryId: string; data: { label: string; amount: number; date: Date } }) => {
      return apiRequest("PATCH", `/api/goals/${goal.id}/progress-entries/${entryId}`, data);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["/api/goals", goal.id, "progress-entries"] });
      queryClient.invalidateQueries({ queryKey: ["/api/goals", goal.id] });
      queryClient.invalidateQueries({ queryKey: ["/api/goals"] });
      setEditedEntries((prev) => {
        const newEdited = { ...prev };
        delete newEdited[variables.entryId];
        return newEdited;
      });
      toast({
        title: "Entry updated",
        description: "Measurement entry has been saved.",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to update measurement entry.",
        variant: "destructive",
      });
    },
  });

  const submitLockRef = useRef(false);
  useEffect(() => {
    if (!createEntryMutation.isPending) submitLockRef.current = false;
  }, [createEntryMutation.isPending]);

  const handleAddEntry = () => {
    if (submitLockRef.current || createEntryMutation.isPending) return;
    const amount = parseFloat(newEntryAmount);
    if (!newEntryLabel.trim() || isNaN(amount)) {
      toast({
        title: "Invalid input",
        description: "Please provide both a label and a valid amount.",
        variant: "destructive",
      });
      return;
    }
    submitLockRef.current = true;
    createEntryMutation.mutate({
      label: newEntryLabel,
      amount,
      date: parseLocalDate(newEntryDate),
    });
  };

  const handleDeleteEntry = (entryId: string) => {
    deleteEntryMutation.mutate(entryId);
  };

  const handleEntryChange = (entryId: string, field: "label" | "amount" | "date", value: string) => {
    const entry = entries.find((e) => e.id === entryId);
    if (!entry) return;
    
    const entryDate = formatDateForInput(entry.date);
    
    setEditedEntries((prev) => ({
      ...prev,
      [entryId]: {
        label: prev[entryId]?.label ?? (entry.label || ""),
        amount: prev[entryId]?.amount ?? String(entry.amount || 0),
        date: prev[entryId]?.date ?? entryDate,
        [field]: value,
      },
    }));
  };

  const handleSaveEntry = (entryId: string) => {
    const edited = editedEntries[entryId];
    if (!edited) return;

    const amount = parseFloat(edited.amount);
    if (!edited.label.trim() || isNaN(amount)) {
      toast({
        title: "Invalid input",
        description: "Please provide both a label and a valid amount.",
        variant: "destructive",
      });
      return;
    }

    updateEntryMutation.mutate({
      entryId,
      data: { label: edited.label, amount, date: parseLocalDate(edited.date) },
    });
  };

  const handleTrackingModeChange = (mode: "sum" | "trend") => {
    setTrackingMode(mode);
    updateTrackingModeMutation.mutate(mode);
  };

  const isEntryModified = (entryId: string) => {
    const entry = entries.find((e) => e.id === entryId);
    const edited = editedEntries[entryId];
    if (!entry || !edited) return false;
    const entryDate = formatDateForInput(entry.date);
    return edited.label !== (entry.label || "") || 
           edited.amount !== String(entry.amount || 0) ||
           edited.date !== entryDate;
  };

  const getEntryLabel = (entry: GoalProgressEntry) => {
    return editedEntries[entry.id]?.label ?? (entry.label || "");
  };

  const getEntryAmount = (entry: GoalProgressEntry) => {
    return editedEntries[entry.id]?.amount ?? String(entry.amount || 0);
  };

  const getEntryDate = (entry: GoalProgressEntry) => {
    if (editedEntries[entry.id]?.date) {
      return editedEntries[entry.id].date;
    }
    return formatDateForInput(entry.date);
  };

  // Calculate total
  const total = entries.reduce((sum, entry) => sum + (entry.amount || 0), 0);

  // Helper to safely parse dates
  const safeParseDate = (date: Date | string | null | undefined): Date | null => {
    if (!date) return null;
    try {
      const parsed = new Date(date);
      return isNaN(parsed.getTime()) ? null : parsed;
    } catch {
      return null;
    }
  };

  // Filter entries with valid dates for chart and trend calculations
  const entriesWithDates = entries.filter(entry => safeParseDate(entry.date) !== null);

  // Prepare chart data - sorted by date, only include entries with valid dates
  const chartData = [...entriesWithDates]
    .sort((a, b) => safeParseDate(a.date)!.getTime() - safeParseDate(b.date)!.getTime())
    .map((entry) => ({
      // Render the stored UTC calendar day, not the local-time conversion
      // (format(safeParseDate(...)) shifts the day back for users west of UTC).
      date: format(parseISO(utcEntryDay(entry.date)), "MMM d"),
      value: entry.amount,
      label: entry.label || "",
    }));

  // Format based on customGoalType
  const formatAmount = (amount: number) => {
    if (goal.customGoalType === "currency") {
      return `$${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
    return amount.toLocaleString('en-US');
  };

  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle>Measured Tracking</CardTitle>
        <CardDescription>
          Track progress for {goal.customGoalTitle || "your custom goal"}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="text-center py-4">
            <div className="w-6 h-6 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Existing entries */}
            {entries.map((entry) => (
              <div
                key={entry.id}
                className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg"
                data-testid={`entry-${entry.id}`}
              >
                <div className="w-36">
                  <Input
                    type="date"
                    value={getEntryDate(entry)}
                    onChange={(e) => handleEntryChange(entry.id, "date", e.target.value)}
                    className="bg-background"
                    data-testid={`input-entry-date-${entry.id}`}
                  />
                </div>
                <div className="flex-1">
                  <Input
                    value={getEntryLabel(entry)}
                    onChange={(e) => handleEntryChange(entry.id, "label", e.target.value)}
                    className="bg-background"
                    data-testid={`input-entry-label-${entry.id}`}
                  />
                </div>
                <div className="w-28">
                  <Input
                    type="number"
                    step={goal.customGoalType === "currency" ? "0.01" : "1"}
                    value={getEntryAmount(entry)}
                    onChange={(e) => handleEntryChange(entry.id, "amount", e.target.value)}
                    className="bg-background text-right"
                    data-testid={`input-entry-amount-${entry.id}`}
                  />
                </div>
                {isEntryModified(entry.id) && (
                  <Button
                    variant="default"
                    size="icon"
                    onClick={() => handleSaveEntry(entry.id)}
                    disabled={updateEntryMutation.isPending}
                    data-testid={`button-save-entry-${entry.id}`}
                  >
                    <Check className="w-4 h-4" />
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleDeleteEntry(entry.id)}
                  data-testid={`button-delete-entry-${entry.id}`}
                >
                  <Trash2 className="w-4 h-4 text-destructive" />
                </Button>
              </div>
            ))}

            {/* Add new entry form */}
            <div className="flex items-center gap-2 p-3 border-2 border-dashed rounded-lg">
              <div className="w-36">
                <Input
                  type="date"
                  value={newEntryDate}
                  onChange={(e) => setNewEntryDate(e.target.value)}
                  data-testid="input-measure-date"
                />
              </div>
              <div className="flex-1">
                <Input
                  placeholder="Description (e.g., Week 1, Jan Sales)"
                  value={newEntryLabel}
                  onChange={(e) => setNewEntryLabel(e.target.value)}
                  data-testid="input-measure-label"
                />
              </div>
              <div className="w-28">
                <Input
                  type="number"
                  step={goal.customGoalType === "currency" ? "0.01" : "1"}
                  placeholder={goal.customGoalType === "currency" ? "0.00" : "0"}
                  value={newEntryAmount}
                  onChange={(e) => setNewEntryAmount(e.target.value)}
                  className="text-right"
                  data-testid="input-measure-amount"
                />
              </div>
              <Button
                onClick={handleAddEntry}
                disabled={submitLockRef.current || createEntryMutation.isPending}
                data-testid="button-add-measure"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add
              </Button>
            </div>

            {/* Tracking Mode Toggle */}
            <div className="p-4 bg-muted/30 rounded-lg">
              <Label className="text-sm font-medium mb-3 block">Tracking Mode</Label>
              <RadioGroup
                value={trackingMode}
                onValueChange={(value) => handleTrackingModeChange(value as "sum" | "trend")}
                className="flex gap-6"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="sum" id="sum-mode" data-testid="radio-sum-mode" />
                  <Label htmlFor="sum-mode" className="flex items-center gap-2 cursor-pointer">
                    <Calculator className="w-4 h-4" />
                    Sum All Lines
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="trend" id="trend-mode" data-testid="radio-trend-mode" />
                  <Label htmlFor="trend-mode" className="flex items-center gap-2 cursor-pointer">
                    <TrendingUp className="w-4 h-4" />
                    Track Trends
                  </Label>
                </div>
              </RadioGroup>
            </div>

            {/* Sum Mode - Total Display */}
            {trackingMode === "sum" && (
              <div className="flex items-center justify-between p-4 bg-primary/10 rounded-lg border-2 border-primary">
                <span className="font-semibold text-lg">Total:</span>
                <span className="font-bold text-2xl" data-testid="text-total-measured">
                  {formatAmount(total)}
                </span>
              </div>
            )}

            {/* Trend Mode - Chart Display */}
            {trackingMode === "trend" && entriesWithDates.length > 0 && (
              <div className="p-4 bg-background rounded-lg border">
                <h4 className="font-semibold mb-4">Trend Over Time</h4>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis 
                        dataKey="date" 
                        tick={{ fontSize: 12 }}
                        className="text-muted-foreground"
                      />
                      <YAxis 
                        tick={{ fontSize: 12 }}
                        className="text-muted-foreground"
                        tickFormatter={(value) => 
                          goal.customGoalType === "currency" 
                            ? `$${value.toLocaleString()}` 
                            : value.toLocaleString()
                        }
                      />
                      <Tooltip 
                        formatter={(value: number) => [formatAmount(value), "Value"]}
                        labelFormatter={(label) => `Date: ${label}`}
                        contentStyle={{
                          backgroundColor: 'hsl(var(--background))',
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px',
                        }}
                      />
                      <Legend />
                      <Bar 
                        dataKey="value" 
                        name="Value"
                        fill="hsl(var(--primary))" 
                        opacity={0.7}
                        radius={[4, 4, 0, 0]}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="value" 
                        name="Trend"
                        stroke="hsl(var(--destructive))" 
                        strokeWidth={2}
                        dot={{ fill: "hsl(var(--destructive))", strokeWidth: 2 }}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
                
                {/* Statistics summary */}
                <div className="mt-4 grid grid-cols-3 gap-4 text-center">
                  <div className="p-3 bg-muted/50 rounded-lg">
                    <div className="text-sm text-muted-foreground">Latest</div>
                    <div className="font-bold text-lg">
                      {entriesWithDates.length > 0 
                        ? formatAmount(
                            [...entriesWithDates].sort((a, b) => 
                              safeParseDate(b.date)!.getTime() - safeParseDate(a.date)!.getTime()
                            )[0].amount
                          )
                        : "-"
                      }
                    </div>
                  </div>
                  <div className="p-3 bg-muted/50 rounded-lg">
                    <div className="text-sm text-muted-foreground">Average</div>
                    <div className="font-bold text-lg">
                      {entriesWithDates.length > 0 
                        ? formatAmount(
                            entriesWithDates.reduce((sum, e) => sum + (e.amount || 0), 0) / entriesWithDates.length
                          )
                        : "-"
                      }
                    </div>
                  </div>
                  <div className="p-3 bg-muted/50 rounded-lg">
                    <div className="text-sm text-muted-foreground">Change</div>
                    <div className={`font-bold text-lg ${
                      entriesWithDates.length >= 2 
                        ? (() => {
                            const sorted = [...entriesWithDates].sort((a, b) => 
                              safeParseDate(a.date)!.getTime() - safeParseDate(b.date)!.getTime()
                            );
                            const first = sorted[0].amount;
                            const last = sorted[sorted.length - 1].amount;
                            const diff = last - first;
                            return diff >= 0 ? "text-green-600" : "text-red-600";
                          })()
                        : ""
                    }`}>
                      {entriesWithDates.length >= 2 
                        ? (() => {
                            const sorted = [...entriesWithDates].sort((a, b) => 
                              safeParseDate(a.date)!.getTime() - safeParseDate(b.date)!.getTime()
                            );
                            const first = sorted[0].amount;
                            const last = sorted[sorted.length - 1].amount;
                            const diff = last - first;
                            const prefix = diff >= 0 ? "+" : "";
                            return prefix + formatAmount(diff);
                          })()
                        : "-"
                      }
                    </div>
                  </div>
                </div>
              </div>
            )}

            {trackingMode === "trend" && entriesWithDates.length === 0 && (
              <div className="p-8 text-center text-muted-foreground bg-muted/30 rounded-lg">
                <TrendingUp className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>Add entries with dates to see trend visualization</p>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
